import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { GreenTick, HotelSample, Location, StarWhite } from '../../../assests/ExportImageIcon'
import Modal from '../../../componants/modal/Modal'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { mapVenueListItem } from '../../../utils/venueFormat'
import { showToast } from '../../../componants/toast/toastService'
import '../../hotel/hotelListing/hotelResults.css'
import '../venueBookingConfirm.css'
import '../venueModals.css'
import './venueSuggestedVenues.css'

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

// GET /vendors/user_app/proposals/{proposal_id}/suggested-venues — response
// shape confirmed live 2026-09-21: {count, next, previous, data: [{id,
// proposal_id, name, address, type, capacity, amenities, pricings, rating,
// cover_photo}]} — identical to GET /users/venues (list), so the shared
// mapVenueListItem mapper (already fixed for the real pricings[] shape)
// handles this correctly with no changes needed. Note: this endpoint's
// path param is the small rfps-list `id` (1, 2, ...), not `proposal_id` —
// see venueBookingConfirm.jsx's mapRfp() for why the two are different.
const VenueSuggestedVenues = () => {
    const navigate = useNavigate()
    const { state } = useLocation()
    // `proposalId` (the route param, the small rfps-list id) is only for the
    // GET above. The bulk-send POST below needs the OTHER id — the larger
    // `proposal_id` (147, 148...) — passed via nav state from
    // venueBookingConfirm.jsx's "Send to More Venues" button. Falls back to
    // `proposalId` only on a cold entry (e.g. a page refresh loses router
    // state) — best-effort, matches this endpoint's likely-wrong id in that
    // edge case rather than blocking the page entirely.
    const { proposalId } = useParams()
    const originalProposalId = state?.originalProposalId ?? proposalId
    const [venues, setVenues] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [selectedIds, setSelectedIds] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [submittedOpen, setSubmittedOpen] = useState(false)

    useEffect(() => {
        if (!proposalId) return
        setLoading(true)
        setLoadError(false)
        service
            .get(endpoints.venues.suggestedVenues.replace('{proposal_id}', proposalId), {}, { silent: true })
            .then(({ data }) => {
                const list = Array.isArray(data?.data) ? data.data : []
                setVenues(list.map((item) => mapVenueListItem(item, HotelSample)))
            })
            .catch(() => {
                setVenues([])
                setLoadError(true)
            })
            .finally(() => setLoading(false))
    }, [proposalId])

    const toggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
    }

    const handleSubmit = () => {
        if (selectedIds.length === 0) {
            showToast('Please select at least one venue', 'error')
            return
        }
        if (submitting) return
        setSubmitting(true)
        service
            .post(endpoints.venues.requestProposalBulk, {
                original_proposal_id: Number(originalProposalId),
                venues: selectedIds,
            })
            .then(() => setSubmittedOpen(true))
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSubmitting(false))
    }

    return (
        <>
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                <BackArrow />
                                <span className='bsSectionTitle'>Send to More Venues</span>
                            </div>
                            <p className='hotelCard__locationNote mb-3'>
                                Select venues to send this same request to — each vendor will send back their own proposal.
                            </p>
                        </div>

                        <div className='col-md-12'>
                            {loading ? (
                                <p className='hotelResultsEmpty'>Loading suggested venues...</p>
                            ) : loadError ? (
                                <p className='hotelResultsEmpty'>Couldn&apos;t load suggested venues right now. Please try again.</p>
                            ) : venues.length === 0 ? (
                                <p className='hotelResultsEmpty'>No suggested venues found for this request.</p>
                            ) : (
                                <div className='hotelList'>
                                    {venues.map((venue) => (
                                        <div
                                            className={`hotelListItem hoverLift ${selectedIds.includes(venue.id) ? 'venueProposalCard--selected' : ''}`}
                                            key={venue.id}
                                            role='button'
                                            tabIndex={0}
                                            onClick={() => toggleSelect(venue.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    toggleSelect(venue.id)
                                                }
                                            }}
                                        >
                                            <div className='hotelCard'>
                                                <div className='hotelCard__media'>
                                                    <img src={venue.images[0]} alt={venue.name} />
                                                    {selectedIds.includes(venue.id) && (
                                                        <span className='venueSuggestedCard__checkBadge'>
                                                            <img src={GreenTick} alt="" />
                                                        </span>
                                                    )}
                                                </div>

                                                <div className='hotelCard__info'>
                                                    <div className='hotelCard__topRow'>
                                                        <div>
                                                            <div className='hotelCard__rating'>
                                                                <span className='hotelCard__ratingBadge'>
                                                                    <img src={StarWhite} alt="" /> {venue.rating}
                                                                </span>
                                                            </div>
                                                            <div className='hotelCard__name'>{venue.name}</div>
                                                            {venue.venueType ? <div className='hotelCard__locationNote'>{venue.venueType}</div> : null}
                                                            {venue.capacity ? <div className='hotelCard__locationNote'>Capacity: {venue.capacity} Guests</div> : null}
                                                        </div>
                                                        <div className='hotelCard__price'>
                                                            <div className='hotelCard__priceValue'>{venue.price}</div>
                                                            {venue.priceUnit ? <div className='hotelCard__priceLabel'>Per {venue.priceUnit}</div> : null}
                                                        </div>
                                                    </div>

                                                    <div className='hotelCard__bottomRow mt-4'>
                                                        <div className='hotelCard__location'>
                                                            <img src={Location} alt="" />
                                                            <div>
                                                                <div className='hotelCard__locationName'>{venue.location}</div>
                                                            </div>
                                                        </div>
                                                        {venue.amenities.length ? (
                                                            <div className='hotelCard__amenities'>
                                                                {venue.amenities.map((amenity) => (
                                                                    <span className='hotelCard__amenityPill' key={amenity}>{amenity}</span>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {venues.length > 0 && (
                            <div className='col-md-12'>
                                <button
                                    type='button'
                                    className='commonBtn w-100 venueCompareBtn'
                                    disabled={selectedIds.length === 0 || submitting}
                                    onClick={handleSubmit}
                                >
                                    {submitting ? 'Sending...' : `Send Request${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Modal open={submittedOpen} onClose={() => navigate('/venue-booking-confirm')} dismissible={false}>
                <div className='venueModal'>
                    <div className='venueModal__icon venueModal__icon--info'>
                        <img src={Location} alt="" width={26} height={26} />
                    </div>
                    <div className='venueModal__title'>Request Sent Successfully!</div>
                    <p className='venueModal__subtitle'>
                        Your request has been sent to the selected venues. Vendors will review your requirements and share their proposals soon.
                    </p>
                    <div className='venueModal__actions venueModal__actions--column'>
                        <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>
                            View Proposals
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default VenueSuggestedVenues
